/**
 * Common `/dye` params — identity, consent, geo, UA, viewport, UTMs — the
 * params every tracked event carries.
 *
 * Key set follows yoto-web's `serializeAnalyticsArgs`. The standalone SDK does
 * not own every content-app dynamic variable, so those keys are still sent
 * with Yoto-style empty/zero defaults.
 *
 * Identity and consent are read live from state each call so consent flips and
 * identity rotations are reflected in the next dispatched event; UTM params
 * come from the page snapshot resolved once at init.
 */

import type { SdkState } from '../state';
import { currentConsent, currentIdentity } from '../state';
import { SDK_VERSION } from '../config/constants';
import type { ConsentStatus } from '../consent/engine';
import type { EventParams } from './track';
import { parseUa } from '../ua/parse';
import { resolvePageParams } from './pageParams';
import type { ResolvedOptions } from '../options';

/** `co` on `/dye`: `1` granted, `0` denied, omitted while consent is still unknown. */
export function consentCoParam(consent: ConsentStatus): string | undefined {
  if (consent === 'granted') return '1';
  if (consent === 'denied') return '0';
  return undefined;
}

function viewportWidth(): number {
  try {
    return typeof window === 'undefined' ? 0 : Math.round(window.innerWidth || 0);
  } catch {
    return 0;
  }
}

function viewportHeight(): number {
  try {
    return typeof window === 'undefined' ? 0 : Math.round(window.innerHeight || 0);
  } catch {
    return 0;
  }
}

function viewportWidthGroup(width: number, platform: string): number {
  const isDesktopView = platform === 'desktop';
  if (isDesktopView) {
    if (width >= 1400) return 6;
    if (width >= 1200) return 5;
    if (width >= 992) return 4;
    if (width >= 768) return 3;
  }
  return width >= 576 ? 2 : 1;
}

function browserSize(): string {
  try {
    return `${viewportWidth()}x${viewportHeight()}`;
  } catch {
    return 'err';
  }
}

function screenResolution(): string {
  try {
    return typeof screen === 'undefined' ? '0x0' : `${screen.width}x${screen.height}`;
  } catch {
    return 'err';
  }
}

export function buildCommonParams(state: SdkState): EventParams {
  const { config, options, ua, pageParams } = state;
  const ids = currentIdentity();
  const consent = currentConsent();
  const language =
    typeof navigator !== 'undefined' ? navigator.language?.split(',')[0] ?? 'en' : 'en';
  const path = typeof location !== 'undefined' ? location.pathname : '/';
  const fullUrl = typeof location !== 'undefined' ? location.href : '';
  const width = viewportWidth();
  const mediaBuyingEnabled = options.mediaBuyingEnabled;

  const out: EventParams = {
    /* SDK_BUNDLE */ sdk: '1',
    /* YOTO_VERSION */ yv: SDK_VERSION,
    /* ACTIVITY_ID */ ac: config.analytics.activityId,
    /* ACTIVITY_MODEL */ acm: config.analytics.activityModel,

    /* REFERRER_HOSTNAME */ h: options.trackingDomain ?? config.website.domain,
    /* COUNTRY_CODE */ cc: config.geo.country,
    // Content locale (language code, e.g. 'en'), NOT the visitor's geo. BI joins
    // locale-translated creative dims (headline, media url) on it. Post payload
    // locale once content arrives; until then the locale the fetch requests.
    /* LOCALE */ lo: state.content.locale ?? options.defaultLocale,
    /* USER_LANGUAGE */ lg: language,
    // Media-buying rows derive layout from the creative ('ecom'); reporting ''
    // for ecom sites splits sessions from spend on the Layout dimension.
    /* LAYOUT */ ly: options.layout ?? (config.ecom.active ? 'ecom' : ''),

    /* USER_ID */ uid: ids.userId,
    /* USER_ID_FREQUENCY */ uidf: ids.uidf ?? 0,
    /* SESSION_ID */ sid: ids.sessionId,
    /* PAGEVIEW_ID */ pvi: ids.pageviewId,

    /* HAS_CONSENT — omitted while consent is unknown */ co: consentCoParam(consent),

    /* ORIGINAL_POST_ID */ opid: options.postId ?? '',
    /* VIEWPORT_WIDTH_GROUP */ vwg: viewportWidthGroup(width, ua.p),

    /* UTM_CAMPAIGN — filled below when media buying is on */ utmc: '',
    /* UTM_CONTENT */ utmco: '',
    /* UTM_MEDIUM */ utmm: '',
    /* UTM_SOURCE */ utms: '',
    /* UTM_TERM */ utmt: '',
    /* MEDIA_BUYING_ID */ mbid: '',
    /* USER_VALUE_OBJECTIVE */ uvo: '0',
    /* PLATFORM_CLICK_ID */ mbclid: '',
    /* ADVERTISER_CAMPAIGN_ID */ aci: pageParams.channel || options.defaultChannel || '',

    /* BROWSER */ b: ua.b,
    /* BROWSER_VERSION */ bv: ua.bv,
    /* OPERATING_SYSTEM */ os: ua.os,
    /* DEVICE */ dev: ua.dev,
    /* PLATFORM */ p: ua.p,

    /* REFERRER_URI */ uri: path,
    /* REFERRER_FULL_URL */ furl: fullUrl,
    /* BROWSER_SIZE */ wh: browserSize(),
    /* SCREEN_RESOLUTION */ sr: screenResolution(),

    /* IIQ_GROUP */ ig: '',
    /* AB_TEST_VALUE */ abtv: pageParams.abTestValue || 'a',
    /* PAGE_NUMBER */ pn: 0,
    /* PAGE_DEPTH */ pd: 0,
    /* ARTICLE_DEPTH */ ard: 0,
    /* POST_ID */ pid: options.postId ?? '',
    /* POST_AD_DENSITY */ adns: '',
    /* COVER_IMAGE_ID */ ciid: '',
    /* POST_ITEM_ID */ iid: '',
    /* ITEM_PAGE_INDEX */ ipi: 0,
    /* ITEM_ARTICLE_INDEX */ iai: 0,
    /* ARTICLE_PAGE_INDEX */ apidx: 0,

    /* cache buster */ _: Date.now(),
  };

  if (mediaBuyingEnabled) {
    out.utmc = pageParams.utmCampaign;
    out.utmco = pageParams.utmContent;
    out.utmm = pageParams.utmMedium;
    out.utms = pageParams.utmSource;
    out.utmt = pageParams.utmTerm;
    out.mbid = pageParams.mediaBuyingId;
    out.uvo = pageParams.userValueObjective;
    out.mbclid = pageParams.platformClickId;
  }

  return out;
}

/**
 * The `buildCommonParams` twin for events dispatched before state exists
 * (dying-page kzq emergency flush). Carries everything resolvable without the
 * config fetch — identity comes from the caller, geo (`cc`) and consent
 * (`co`) are unknowable and stay out. Anything filterable that IS resolvable
 * must be here: a missing `abtv` lands the event outside every ab_test_value
 * segment, making rescued traffic invisible next to its READY-path siblings.
 */
export function buildEmergencyCommonParams(
  options: ResolvedOptions,
  ids: { userId: string; sessionId: string; pageviewId: string },
): EventParams {
  const ua = parseUa();
  const pageParams = resolvePageParams();
  const language =
    typeof navigator !== 'undefined' ? navigator.language?.split(',')[0] ?? 'en' : 'en';
  const width = viewportWidth();

  const out: EventParams = {
    sdk: '1',
    yv: SDK_VERSION,
    ac: options.activityId ?? '',
    acm: options.activityModel ?? '',
    h: options.trackingDomain ?? options.domain,
    /* content locale — the fetch-request locale; no post payload this early */
    lo: options.defaultLocale,
    ly: options.layout ?? '',

    uid: ids.userId,
    uidf: 0,
    sid: ids.sessionId,
    pvi: ids.pageviewId,

    opid: options.postId ?? '',
    vwg: viewportWidthGroup(width, ua.p),

    utmc: '',
    utmco: '',
    utmm: '',
    utms: '',
    utmt: '',
    mbid: '',
    uvo: '0',
    mbclid: '',
    aci: pageParams.channel || options.defaultChannel || '',

    b: ua.b,
    bv: ua.bv,
    os: ua.os,
    dev: ua.dev,
    p: ua.p,

    uri: typeof location !== 'undefined' ? location.pathname : '/',
    furl: typeof location !== 'undefined' ? location.href : '',
    wh: browserSize(),
    sr: screenResolution(),
    lg: language,

    ig: '',
    abtv: pageParams.abTestValue || 'a',
    pn: 0,
    pd: 0,
    ard: 0,
    pid: options.postId ?? '',
    adns: '',
    ciid: '',
    iid: '',
    ipi: 0,
    iai: 0,
    apidx: 0,
  };

  if (options.mediaBuyingEnabled) {
    out.utmc = pageParams.utmCampaign;
    out.utmco = pageParams.utmContent;
    out.utmm = pageParams.utmMedium;
    out.utms = pageParams.utmSource;
    out.utmt = pageParams.utmTerm;
    out.mbid = pageParams.mediaBuyingId;
    out.uvo = pageParams.userValueObjective;
    out.mbclid = pageParams.platformClickId;
  }

  return out;
}
